import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Autocomplete, Stack, TextField, Typography, Popper } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flightAirportsAreSame } from 'legacyfrontend/helpers/flightAirportsAreSame';
import { saveResolvedFlightInfo } from 'legacyfrontend/flights/search/flightSearchStorage';
import {
  MIN_AIRPORT_QUERY_LENGTH,
  useFlightAirportOptions,
} from 'legacyfrontend/flights/search/useFlightAirportOptions';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectPaisId } from 'legacyfrontend/redux/selectors/application-selectors';
import Switch from 'react-switch';
import ErrorSnackbar from '../ErrorSnackbar';
import DatePickerFullScreenDialog from './DatePickerFullScreenDialog';
import DateRangeSelector from './DateRangeSelector';
import OrigenDestinoFullScreenDialog from './OrigenDestinoFullScreenDialog';
import PasajerosFullScreenDialog from './PasajerosFullScreenDialog';
import PasajerosSelector from './PasajerosSelector';
import { addYearsToDate, getDisabledDates } from 'legacyfrontend/utils/dateUtils';

const sortByCountryFirstOccurrence = (airports) => {
  const firstIndexByCountry = {};
  airports.forEach((a, i) => {
    if (firstIndexByCountry[a.countryCode] == null) {
      firstIndexByCountry[a.countryCode] = i;
    }
  });
  return airports
    .map((a, i) => ({ a, i }))
    .sort((x, y) => {
      const diff = firstIndexByCountry[x.a.countryCode] - firstIndexByCountry[y.a.countryCode];
      return diff !== 0 ? diff : x.i - y.i;
    })
    .map(({ a }) => a);
};

const FlightsFilterForm = ({
  handleOriginSelect,
  handleDestinationSelect,
  handleDateRangeSelect,
  handleOccupationChange,
  handleFlightPassenger,
  formInput,
  isDestinationEmpty,
  setIsDestinationEmpty,
  isOriginEmpty,
  setIsOriginEmpty,
  isDatesEmpty,
  isTuristasEmpty,
  setIsDatesEmpty,
  handleIsOnlyOneWay,
  handleCurrencyChange,
  onMobileSearchComplete,
  isMobileSearchActive = false,
  isLoading = false,
}) => {
  const { t } = useTranslation('translations');
  const maxPassengers = 9;
  const paisId = useSelector(selectPaisId);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [totalTourists, setTotalTourists] = useState(1);
  const [adultsQuantity, setAdultsQuantity] = useState(1);
  const [childrenQuantity, setChildrenQuantity] = useState(0);
  const [babiesQuantity, setBabiesQuantity] = useState(0);
  const [passengerAges, setPassengerAges] = useState([30]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [isFullScreenRangeDatePickerOpen, setIsFullScreenRangeDatePickerOpen] = useState(false);
  const [shouldPasajerosOpen, setShouldPasajerosOpen] = useState(false);
  const [isOriginFocused, setIsOriginFocused] = useState(false);
  const [isDestinationFocused, setIsDestinationFocused] = useState(false);
  const [isOnlyOneWay, setIsOnlyOneWay] = useState(formInput?.isOnlyOneWay ?? false);
  const [currency, setCurrency] = useState(formInput?.currency || 'USD');

  const [isFirstSearch, setIsFirstSearch] = useState(true);

  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const isInitialMount = useRef(true);
  const isUpdatingFromFormInput = useRef(false);
  const prevStartDate = useRef(null);
  const prevEndDate = useRef(null);
  const pasajerosRef = useRef(null);

  const totalPassengers = adultsQuantity + childrenQuantity + babiesQuantity;
  const countryDisplayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames(['es', 'en'], { type: 'region' });
    } catch {
      return null;
    }
  }, []);

  const getCountryLabel = useCallback(
    option => {
      if (option?.countryName) return option.countryName;
      const code = option?.countryCode;
      if (!code) return 'Otros';
      const translated = countryDisplayNames?.of(code);
      return translated || code;
    },
    [countryDisplayNames]
  );

  const safeDate = (val) => {
    if (!val) return undefined;
    const d = val instanceof Date ? val : new Date(val);
    return isNaN(d) ? undefined : d;
  };

  const getAirportOptionLabel = useCallback((option) => {
    if (!option || typeof option !== 'object') return '';
    const cityName = option.cityName || '';
    const airportName = option.airportName || option.cityName || '';
    const airportCode = option.airportCode || option.cityCode || '';
    return `${cityName} - ${airportName} (${airportCode})`;
  }, []);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const {
    flightOrigins,
    flightDestinations,
    searchOrigin,
    searchDestination,
    defaultOriginOptions,
    defaultDestOptions,
    originSuggestionsLoaded,
    destSuggestionsLoaded,
    handleSearchChange,
    setOriginSearchValue,
    setDestinationSearchValue,
    trackOriginSearchValue,
    trackDestinationSearchValue,
  } = useFlightAirportOptions({ showError });

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleLlegoData = useCallback(
    (passengers, startDate, endDate) => {
      handleFlightPassenger(passengers);
    },
    [handleFlightPassenger]
  );

  const sortedFlightOrigins = (() => {
    const t = searchOrigin.trim();
    const base =
      t.length === 0
        ? defaultOriginOptions
        : t.length >= MIN_AIRPORT_QUERY_LENGTH
          ? (flightOrigins?.data || [])
          : [];
    const sorted = sortByCountryFirstOccurrence(base);
    if (selectedOrigin && !sorted.find((o) => flightAirportsAreSame(o, selectedOrigin))) {
      return [selectedOrigin, ...sorted];
    }
    return sorted;
  })();

  const sortedFlightDestinations = (() => {
    const t = searchDestination.trim();
    const base =
      t.length === 0
        ? defaultDestOptions
        : t.length >= MIN_AIRPORT_QUERY_LENGTH
          ? (flightDestinations?.data || [])
          : [];
    const sorted = sortByCountryFirstOccurrence(base);
    if (selectedDestination && !sorted.find((o) => flightAirportsAreSame(o, selectedDestination))) {
      return [selectedDestination, ...sorted];
    }
    return sorted;
  })();

  const originNoOptionsText =
    searchOrigin.trim().length < MIN_AIRPORT_QUERY_LENGTH
      ? `Escribí al menos ${MIN_AIRPORT_QUERY_LENGTH} letras para buscar`
      : 'No hay resultados';

  const destinationNoOptionsText =
    searchDestination.trim().length < MIN_AIRPORT_QUERY_LENGTH
      ? `Escribí al menos ${MIN_AIRPORT_QUERY_LENGTH} letras para buscar`
      : 'No hay resultados';

  const restoreDefaultSuggestionsOnFocus = useCallback(
    (isOrigin) => {
      const input = isOrigin ? originInputRef.current : destinationInputRef.current;
      const selectedValue = isOrigin ? selectedOrigin : selectedDestination;
      const rawValue = typeof input?.value === 'string' ? input.value : '';

      if (selectedValue || rawValue.trim().length > 0) return;

      handleSearchChange('', isOrigin);
    },
    [handleSearchChange, selectedOrigin, selectedDestination]
  );

  const handleOriginCountryCode = (value) => {
    saveResolvedFlightInfo({
      originCountryCode: value.countryCode,
      originId: value.id,
      originName: value.cityName,
      originAirportCode: value.airportCode,
      originAirportName: value.airportName,
      originCityCode: value.cityCode,
    });
  };

  const handleDestinationCountryCode = (value) => {
    saveResolvedFlightInfo({
      destinationCountryCode: value.countryCode,
      destinationId: value.id,
      destinationName: value.cityName,
      destinationAirportCode: value.airportCode,
      destinationAirportName: value.airportName,
      destinationCityCode: value.cityCode,
    });
  };

  const handleChangeOrigin = (value) => {
    handleOriginSelect(value);
    setSelectedOrigin(value);
    setOriginSearchValue(value ? getAirportOptionLabel(value) : '');
    trackOriginSearchValue(value ? value.cityName || '' : '');
    setIsOriginEmpty(false);
    if (originInputRef.current) originInputRef.current.blur();
    if (value) {
      handleOriginCountryCode(value);
    }
    if (value) {
      if (selectedDestination) {
        setFocusedInput('startDate');
        if (isMobile) setIsFullScreenRangeDatePickerOpen(true);
      } else {
        if (destinationInputRef.current) {
          setTimeout(() => {
            destinationInputRef.current.focus();
          }, 200);
        }
        setIsDestinationOpen(true);
      }
    }
    return undefined;
  };

  const handleChangeDestination = (value) => {
    handleDestinationSelect(value);
    setSelectedDestination(value);
    setDestinationSearchValue(value ? getAirportOptionLabel(value) : '');
    trackDestinationSearchValue(value ? value.cityName || '' : '');
    setIsDestinationEmpty(false);
    if (value) {
      setIsDestinationOpen(false);
      handleDestinationCountryCode(value);
      if (!startDate) {
        setFocusedInput('startDate');
        if (isMobile) setIsFullScreenRangeDatePickerOpen(true);
      } else if (startDate && !endDate && !formInput.isOnlyOneWay) {
        setFocusedInput('endDate');
        if (isMobile) setIsFullScreenRangeDatePickerOpen(true);
      }
    }
  };

  const handleOriginInputChange = (value, reason) => {
    if (reason !== 'input' && reason !== 'clear') return;

    if (reason === 'clear') {
      handleChangeOrigin(null);
      handleSearchChange('', true);
      return;
    }

    const nextValue = value ?? '';
    const selectedLabel = selectedOrigin ? getAirportOptionLabel(selectedOrigin) : '';

    if (selectedOrigin && nextValue !== selectedLabel) {
      handleChangeOrigin(null);
    }

    handleSearchChange(nextValue, true);
  };

  const handleDestinationInputChange = (value, reason) => {
    if (reason !== 'input' && reason !== 'clear') return;

    if (reason === 'clear') {
      handleChangeDestination(null);
      handleSearchChange('', false);
      return;
    }

    const nextValue = value ?? '';
    const selectedLabel = selectedDestination ? getAirportOptionLabel(selectedDestination) : '';

    if (selectedDestination && nextValue !== selectedLabel) {
      handleChangeDestination(null);
    }

    handleSearchChange(nextValue, false);
  };

  const handleSwitchChange = useCallback(
    (checked) => {
      setIsOnlyOneWay(checked);
      handleIsOnlyOneWay(checked);
      if (checked) {
        setEndDate(null);
      }
    },
    [handleIsOnlyOneWay]
  );

  const handleCurrencySwitchChange = useCallback(
    (checked) => {
      const newCurrency = checked ? 'ARS' : 'USD';
      setCurrency(newCurrency);
      if (handleCurrencyChange) {
        handleCurrencyChange(newCurrency);
      }
    },
    [handleCurrencyChange]
  );

  const handlePassengerChange = useCallback(
    (type, value) => {
      const newQuantity = parseInt(value, 10) || 0;
      const otherQuantities =
        type === 'adults'
          ? childrenQuantity + babiesQuantity
          : type === 'children'
            ? adultsQuantity + babiesQuantity
            : adultsQuantity + childrenQuantity;

      if (newQuantity + otherQuantities <= maxPassengers) {
        switch (type) {
          case 'adults':
            setAdultsQuantity(newQuantity);
            break;
          case 'children':
            setChildrenQuantity(newQuantity);
            break;
          case 'babies':
            setBabiesQuantity(newQuantity);
            break;
        }
      } else {
        showError(`El número total de pasajeros no puede superar ${maxPassengers}.`);
      }
    },
    [adultsQuantity, childrenQuantity, babiesQuantity, showError]
  );

  const handlePasajerosClose = useCallback(() => {
    if (pasajerosRef.current) {
      pasajerosRef.current.setAttribute('inert', '');
    }
    setShouldPasajerosOpen(false);
    setIsFirstSearch(false);
    if (onMobileSearchComplete) {
      onMobileSearchComplete();
    }
  }, [onMobileSearchComplete]);

  const handlePasajerosOpen = useCallback(() => {
    if (isLoading) {
      return;
    }
    if (selectedOrigin && selectedDestination) {
      if (pasajerosRef.current) {
        pasajerosRef.current.removeAttribute('inert');
      }
      setShouldPasajerosOpen(true);
    }
  }, [selectedOrigin, selectedDestination, isLoading]);

  useEffect(() => {
    const {
      departureDate: newDeparture,
      returnDate: newReturn,
      isOnlyOneWay: newIsOnlyOneWay,
      currency: newCurrency,
    } = formInput;

    isUpdatingFromFormInput.current = true;

    const departureTime = (() => {
      if (newDeparture == null || newDeparture === '') return NaN;
      const d = newDeparture instanceof Date ? newDeparture : new Date(newDeparture);
      return d.getTime();
    })();
    if (!Number.isNaN(departureTime)) {
      setStartDate(newDeparture instanceof Date ? newDeparture : new Date(newDeparture));
    } else {
      setStartDate(null);
    }

    if (!newIsOnlyOneWay && newReturn != null && newReturn !== '') {
      const ret = newReturn instanceof Date ? newReturn : new Date(newReturn);
      if (!Number.isNaN(ret.getTime())) {
        setEndDate(ret);
      } else {
        setEndDate(null);
      }
    } else {
      setEndDate(null);
    }

    if (newIsOnlyOneWay !== undefined && newIsOnlyOneWay !== isOnlyOneWay) {
      setIsOnlyOneWay(newIsOnlyOneWay);
    }

    if (newCurrency && newCurrency !== currency) {
      setCurrency(newCurrency);
    }

    setTimeout(() => {
      isUpdatingFromFormInput.current = false;
      isInitialMount.current = false;
    }, 0);
  }, [formInput.departureDate, formInput.returnDate, formInput.isOnlyOneWay, formInput.currency, currency, isOnlyOneWay]);

  useEffect(() => {
    if (
      isInitialMount.current ||
      isUpdatingFromFormInput.current ||
      (startDate === prevStartDate.current && endDate === prevEndDate.current)
    ) {
      prevStartDate.current = startDate;
      prevEndDate.current = endDate;
      return;
    }

    handleDateRangeSelect(startDate, endDate);

    if (formInput.isOnlyOneWay && startDate) {
      handleDateRangeSelect(startDate, null);
      setFocusedInput(null);
    }
    prevStartDate.current = startDate;
    prevEndDate.current = endDate;
  }, [startDate, endDate, formInput.isOnlyOneWay]);

  useEffect(() => {
    if (passengerAges.length > 0 && !isInitialMount.current && !isUpdatingFromFormInput.current) {
      const formatDateLocal = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const startOnly = startDate ? formatDateLocal(startDate) : null;
      const endOnly =
        !formInput.isOnlyOneWay && endDate ? formatDateLocal(endDate) : null;

      handleLlegoData(passengerAges, startOnly, endOnly);
    }
  }, [passengerAges, startDate, endDate, formInput.isOnlyOneWay]);

  useEffect(() => {
    if (startDate && endDate) setIsDatesEmpty(false);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = isClient ? window.innerWidth <= 768 : false;
      setIsMobile(prevIsMobile => {
        if (newIsMobile !== prevIsMobile) {
          return newIsMobile;
        }
        return prevIsMobile;
      });
    };
    handleResize();
    isClient && window.addEventListener('resize', handleResize);

    return () => isClient && window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const o = formInput?.origin;
    if (o?.cityCode && String(o.cityName || '').trim()) {
      setSelectedOrigin((prev) => {
        if (prev?.cityCode === o.cityCode && flightAirportsAreSame(prev, o)) return prev;
        return o;
      });
      setOriginSearchValue(getAirportOptionLabel(o));
      trackOriginSearchValue(o.cityName || '');
    } else {
      setSelectedOrigin(null);
      setOriginSearchValue('');
    }
  }, [formInput?.origin, getAirportOptionLabel, setOriginSearchValue, trackOriginSearchValue]);

  useEffect(() => {
    const d = formInput?.destination;
    if (d?.cityCode && String(d.cityName || '').trim()) {
      setSelectedDestination((prev) => {
        if (prev?.cityCode === d.cityCode && flightAirportsAreSame(prev, d)) return prev;
        return d;
      });
      setDestinationSearchValue(getAirportOptionLabel(d));
      trackDestinationSearchValue(d.cityName || '');
    } else {
      setSelectedDestination(null);
      setDestinationSearchValue('');
    }
  }, [formInput?.destination, getAirportOptionLabel, setDestinationSearchValue, trackDestinationSearchValue]);

  useEffect(() => {
    setTotalTourists(totalPassengers);
  }, [totalPassengers]);

  useEffect(() => {
    if (totalTourists > 0) {
      handleOccupationChange(totalTourists);
    }
  }, [totalTourists]);

  useEffect(() => {
    const adultsAges = Array(adultsQuantity).fill(30);
    const childrenAges = Array(childrenQuantity).fill(7);
    const babiesAges = Array(babiesQuantity).fill(1);

    setPassengerAges([...adultsAges, ...childrenAges, ...babiesAges]);
  }, [adultsQuantity, childrenQuantity, babiesQuantity]);

  useEffect(() => {
    const urlParams = new URLSearchParams(isClient ? window.location.search : '');
    const passengersParam = urlParams.get('passengers');

    if (passengersParam) {
      try {
        const decodedPassengers = decodeURIComponent(passengersParam);
        const passengersArray = decodedPassengers.split(',').map(Number);

        if (passengersArray.length > 0) {
          const adults = passengersArray.filter((age) => age === 30).length;
          const children = passengersArray.filter((age) => age === 7).length;
          const babies = passengersArray.filter((age) => age === 1).length;

          setAdultsQuantity(adults || 1);
          setChildrenQuantity(children || 0);
          setBabiesQuantity(babies || 0);
        }
      } catch (error) {
        setAdultsQuantity(1);
      }
    }
  }, []);

  useEffect(() => {
    if (adultsQuantity === 0 && childrenQuantity === 0 && babiesQuantity === 0) {
      setAdultsQuantity(1);
    }
  }, [adultsQuantity, childrenQuantity, babiesQuantity]);

  useEffect(() => {
    if (isLoading) {
      if (shouldPasajerosOpen) {
        handlePasajerosClose();
      }
      if (isDestinationDialogOpen) {
        setIsDestinationDialogOpen(false);
      }
      if (isFullScreenRangeDatePickerOpen) {
        setIsFullScreenRangeDatePickerOpen(false);
      }
    }
  }, [isLoading, shouldPasajerosOpen, handlePasajerosClose, isDestinationDialogOpen, isFullScreenRangeDatePickerOpen]);

  useEffect(() => {
    if (!selectedOrigin || !selectedDestination) {
      if (pasajerosRef.current) {
        pasajerosRef.current.setAttribute('inert', '');
      }
      setShouldPasajerosOpen(false);
    } else if (pasajerosRef.current) {
      pasajerosRef.current.removeAttribute('inert');
    }
  }, [selectedOrigin, selectedDestination]);

  const ListboxProps = useMemo(
    () => ({
      style: {
        maxHeight: '400px',
        width: 'max-content',
        minWidth: '200px',
        maxWidth: '350px',
      },
    }),
    []
  );

  const PopperComponent = useMemo(
    () => (props) => (
      <Popper
        {...props}
        style={{
          ...props.style,
          width: 'max-content',
          minWidth: '200px',
          maxWidth: '350px',
        }}
        placement='bottom-start'
      />
    ),
    []
  );

  const renderGroup = useMemo(
    () => (params) => (
      <li key={`${params.group}-${params.children?.length || 0}`}>
        <div
          style={{
            padding: '8px 16px',
            fontWeight: 500,
            color: '#666',
          }}
        >
          {params.group}
        </div>
        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
      </li>
    ),
    []
  );

  return (
    <>
      <Stack className='input-container' sx={{ position: 'relative' }}>
        <Typography color='#4f4f4f' fontSize={12} sx={{ lineHeight: '18px', minHeight: '18px' }} variant='h3'>
          ¿Desde dónde?
        </Typography>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            handleAirportChange={(_, value) => handleSearchChange(value, true)}
            isFlight
            isLoading={flightOrigins?.isLoading}
            isOriginEmpty={isOriginEmpty}
            onSelect={handleChangeOrigin}
            options={sortedFlightOrigins}
            originSelected={selectedOrigin}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsOriginEmpty={setIsOriginEmpty}
            autoOpen={!isMobileSearchActive}
            disabled={isLoading}
          />
        ) : (
          <Autocomplete
            ListboxProps={ListboxProps}
            PopperComponent={PopperComponent}
            className={`form__field origin-typeahead ${isOriginEmpty ? 'autocomplete-error' : ''}`}
            clearOnBlur
            disabled={isLoading}
            getOptionLabel={getAirportOptionLabel}
            groupBy={option => getCountryLabel(option)}
            id='origin-autocomplete'
            isOptionEqualToValue={(option, value) =>
              Boolean(option && value && flightAirportsAreSame(option, value))
            }
            loading={flightOrigins?.isLoading}
            loadingText='Cargando orígenes'
            noOptionsText={originNoOptionsText}
            onChange={(_, v) => handleChangeOrigin(v)}
            onClose={() => setIsOriginFocused(false)}
            onFocus={() => {
              setIsOriginEmpty(false);
              setIsOriginFocused(true);
              restoreDefaultSuggestionsOnFocus(true);
            }}
            onInputChange={(_, value, reason) => handleOriginInputChange(value, reason)}
            inputValue={searchOrigin}
            open={
              isOriginFocused &&
              (flightOrigins?.isLoading ||
                sortedFlightOrigins.length > 0 ||
                searchOrigin.trim().length > 0 ||
                (originSuggestionsLoaded && searchOrigin.trim().length === 0 && !flightOrigins?.isLoading))
            }
            options={sortedFlightOrigins}
            inputValue={searchOrigin}
            renderGroup={renderGroup}
            renderInput={(params) => (
              <TextField
                {...params}
                className='focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent'
                inputRef={(input) => (originInputRef.current = input)}
                onClick={() => {
                  setIsOriginEmpty(false);
                  setIsOriginFocused(true);
                  restoreDefaultSuggestionsOnFocus(true);
                }}
                onFocus={() => {
                  if (!selectedOrigin) handleOriginSelect(undefined);
                }}
              />
            )}
            size='small'
            style={{ width: '100%' }}
            value={selectedOrigin}
          />
        )}
        <Stack
          flexDirection='row'
          alignItems='center'
          display={isOriginEmpty ? 'flex' : 'none'}
          gap={0.5}
          sx={{ position: 'absolute', left: 0, top: '100%', mt: '2px' }}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography color='rgb(207, 24, 0)' fontSize={11}>
            Ingresa tu origen
          </Typography>
        </Stack>
      </Stack>

      <Stack className='input-container' sx={{ position: 'relative' }}>
        <Typography color='#4f4f4f' fontSize={12} sx={{ lineHeight: '18px', minHeight: '18px' }} variant='h3'>
          ¿Hasta dónde?
        </Typography>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            disabled={isLoading}
            handleAirportChange={(_, value) => handleSearchChange(value, false)}
            isDestination
            isDestinationEmpty={isDestinationEmpty}
            isFlight
            isLoading={flightDestinations?.isLoading}
            isOpen={isDestinationDialogOpen}
            onSelect={handleChangeDestination}
            options={sortedFlightDestinations}
            originSelected={selectedDestination}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsDestinationEmpty={setIsDestinationEmpty}
            setIsFullScreenRangeDatePickerOpen={setIsFullScreenRangeDatePickerOpen}
            autoOpen={!isMobileSearchActive}
          />
        ) : (
          <Autocomplete
            ListboxProps={ListboxProps}
            PopperComponent={PopperComponent}
            className={`form__field destination-typeahead ${isDestinationEmpty ? 'autocomplete-error' : ''}`}
            clearOnBlur
            disabled={isLoading}
            getOptionLabel={getAirportOptionLabel}
            groupBy={option => getCountryLabel(option)}
            id='destination-autocomplete'
            isOptionEqualToValue={(option, value) =>
              Boolean(option && value && flightAirportsAreSame(option, value))
            }
            loading={flightDestinations?.isLoading}
            loadingText='Cargando destinos'
            noOptionsText={destinationNoOptionsText}
            onChange={(e, v) => handleChangeDestination(v)}
            onClose={() => setIsDestinationFocused(false)}
            onFocus={() => {
              setIsDestinationEmpty(false);
              setIsDestinationFocused(true);
              restoreDefaultSuggestionsOnFocus(false);
            }}
            onInputChange={(_, value, reason) => handleDestinationInputChange(value, reason)}
            inputValue={searchDestination}
            open={
              isDestinationFocused &&
              (flightDestinations?.isLoading ||
                sortedFlightDestinations.length > 0 ||
                searchDestination.trim().length > 0 ||
                (destSuggestionsLoaded && searchDestination.trim().length === 0 && !flightDestinations?.isLoading))
            }
            options={sortedFlightDestinations}
            inputValue={searchDestination}
            renderGroup={renderGroup}
            renderInput={(params) => (
              <TextField
                {...params}
                className='focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent'
                inputRef={(input) => (destinationInputRef.current = input)}
                onClick={() => {
                  setIsDestinationEmpty(false);
                  setIsDestinationFocused(true);
                  restoreDefaultSuggestionsOnFocus(false);
                }}
                onFocus={() => {
                  if (!selectedDestination) handleDestinationSelect(undefined);
                }}
              />
            )}
            size='small'
            style={{ width: '100%' }}
            value={selectedDestination}
          />
        )}
        <Stack
          alignItems='center'
          className='warning-animated-message'
          flexDirection='row'
          display={isDestinationEmpty ? 'flex' : 'none'}
          gap={0.5}
          sx={{ position: 'absolute', left: 0, top: '100%', mt: '2px' }}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography color='rgb(207, 24, 0)' fontSize={11}>
            Ingresa tu destino
          </Typography>
        </Stack>
      </Stack>

      <Stack className={`form__field date-picker ${isDatesEmpty ? 'dates-error' : ''}`} sx={{ justifyContent: 'flex-start', position: 'relative' }}>
        {!isMobile && (
          <Stack flexDirection='row' justifyContent='space-between' gap={3} sx={{ alignItems: 'center', minHeight: '18px', width: '100%' }}>
            <Stack alignItems='center' flexDirection='row' gap={1} sx={{ flex: '1 1 0', minWidth: 0 }}>
              <Typography color='#4f4f4f' fontSize={12} sx={{ whiteSpace: 'nowrap' }}>
                Solo ida
              </Typography>
              <Switch 
                checked={isOnlyOneWay} 
                height={16} 
                onChange={handleSwitchChange} 
                width={32}
                disabled={isLoading}
              />
            </Stack>
            {paisId === 1 && (
              <Stack alignItems='center' flexDirection='row' gap={1} sx={{ flex: '1 1 0', justifyContent: 'flex-end', minWidth: 0 }}>
                <Typography color='#4f4f4f' fontSize={12} sx={{ whiteSpace: 'nowrap' }}>
                  USD
                </Typography>
                <Switch
                  checked={currency === 'ARS'}
                  height={16}
                  onChange={handleCurrencySwitchChange}
                  width={32}
                  onColor='#87CEEB'
                  offColor='#28a745'
                  checkedIcon={false}
                  uncheckedIcon={false}
                  disabled={isLoading}
                />
                <Typography color='#4f4f4f' fontSize={12} sx={{ whiteSpace: 'nowrap' }}>
                  ARS
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
        {isMobile ? (
          <DatePickerFullScreenDialog
            endDate={isOnlyOneWay ? undefined : safeDate(endDate)}
            handleSwitchChange={handleSwitchChange}
            handleCurrencySwitchChange={handleCurrencySwitchChange}
            currency={currency}
            isDatesEmpty={isDatesEmpty}
            isOnlyOneWay={isOnlyOneWay}
            isOpen={isFullScreenRangeDatePickerOpen}
            onChange={({ startDate: newStart, endDate: newEnd }) => {
              setStartDate(newStart);
              setEndDate(isOnlyOneWay ? null : newEnd);
              if (!isOnlyOneWay && newEnd && newEnd !== newStart) {
                setIsFullScreenRangeDatePickerOpen(false);
              }
            }}
            setShouldPasajerosOpen={setShouldPasajerosOpen}
            startDate={safeDate(startDate)}
            isLoading={isLoading}
          />
        ) : (
          <DateRangeSelector
            startDate={safeDate(startDate)}
            endDate={isOnlyOneWay ? undefined : safeDate(endDate)}
            onChange={({ startDate: newStart, endDate: newEnd }) => {
              setStartDate(newStart);
              setEndDate(isOnlyOneWay ? null : newEnd);
            }}
            error={isDatesEmpty}
            helperText=''
            disabled={isLoading}
            disabledDates={getDisabledDates()}
            isOneWay={isOnlyOneWay}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
            minDate={new Date()}
          />
        )}
        <Stack
          alignItems='center'
          flexDirection='row'
          display={isDatesEmpty ? 'flex' : 'none'}
          gap={0.5}
          sx={{ position: 'absolute', left: 0, top: '100%', mt: '2px' }}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography color='rgb(207, 24, 0)' fontSize={11}>
            {isDatesEmpty
              ? isOnlyOneWay
                ? 'Selecciona la fecha de ida'
                : 'Selecciona las fechas'
              : ''}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        className={`pasajeros-input-container ${!selectedOrigin || !selectedDestination || isLoading ? 'disabled' : ''
          }`}
        ref={pasajerosRef}
        sx={{
          justifyContent: 'flex-start',
          opacity: !selectedOrigin || !selectedDestination || isLoading ? 0.5 : 1,
          pointerEvents: !selectedOrigin || !selectedDestination || isLoading ? 'none' : 'auto',
          cursor: !selectedOrigin || !selectedDestination || isLoading ? 'not-allowed' : 'pointer',
        }}
      >
        <Typography color='#4f4f4f' fontSize={12} sx={{ lineHeight: '18px', minHeight: '18px' }} variant='h3'>
          ¿Cuántos viajan?
        </Typography>
        <Stack>
          {isMobile ? (
            <PasajerosFullScreenDialog
              adultsQuantity={adultsQuantity}
              babiesQuantity={babiesQuantity}
              childrenQuantity={childrenQuantity}
              disabled={!selectedOrigin || !selectedDestination || isLoading}
              errorMessage={errorMessage}
              onClose={handlePasajerosClose}
              onOpen={handlePasajerosOpen}
              setAdultsQuantity={(value) => handlePassengerChange('adults', value)}
              setBabiesQuantity={(value) => handlePassengerChange('babies', value)}
              setChildrenQuantity={(value) => handlePassengerChange('children', value)}
              setTotalTourists={setTotalTourists}
              shouldOpen={shouldPasajerosOpen && selectedOrigin && selectedDestination}
              totalTourists={totalTourists}
            />
          ) : (
            <PasajerosSelector
              adultsQuantity={adultsQuantity}
              babiesQuantity={babiesQuantity}
              childrenQuantity={childrenQuantity}
              disabled={!selectedOrigin || !selectedDestination || isLoading}
              isTuristasEmpty={isTuristasEmpty}
              onClick={handlePasajerosOpen}
              onClose={handlePasajerosClose}
              setAdultsQuantity={(value) => handlePassengerChange('adults', value)}
              setBabiesQuantity={(value) => handlePassengerChange('babies', value)}
              setChildrenQuantity={(value) => handlePassengerChange('children', value)}
              totalTourists={totalTourists}
            />
          )}
        </Stack>
        <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
      </Stack>
    </>
  );
};

export default FlightsFilterForm;
